import { useState } from "react";
import { Link, useParams } from "wouter";
import {
  useGetQuotes,
  getGetQuotesQueryKey,
  useGetAuthorProfile,
  getGetAuthorProfileQueryKey,
} from "@workspace/api-client-react";
import { QuoteCard } from "@/components/QuoteCard";
import { MetricBar } from "@/components/MetricBar";
import { usePageMeta } from "@/hooks/usePageMeta";

function toCardQuote(q: { id: number; text: string; author: string; era: string; year?: number | null; tags: { name: string }[] }) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
  };
}

function ProfilePendingCard() {
  return (
    <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500">
        Theme Profile
      </h3>
      <p className="text-sm text-stone-400">Profile computing — check back shortly.</p>
    </div>
  );
}

function ProfileInsufficientDataCard() {
  return (
    <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500">
        Theme Profile
      </h3>
      <p className="text-sm text-stone-400">
        Quote profiles are only generated for authors with enough quotes in the library.
      </p>
    </div>
  );
}

function ThemeProfileSection({ clusters }: { clusters: { label: string; percentage: number }[] }) {
  const [expanded, setExpanded] = useState(false);
  if (clusters.length === 0) return null;
  const visible = expanded ? clusters : clusters.slice(0, 3);
  const hasMore = clusters.length > 3;
  return (
    <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-stone-500">
        Theme Profile
      </h3>
      <div className="space-y-3">
        {visible.map((c) => (
          <MetricBar key={c.label} label={c.label} value={c.percentage} />
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-4 text-xs font-medium text-stone-400 transition hover:text-stone-600 focus-visible:outline-none"
        >
          {expanded ? "Show less" : `Show all ${clusters.length}`}
        </button>
      )}
    </div>
  );
}

function SimilarAuthorsSection({ authors }: { authors: { name: string; quoteCount: number }[] }) {
  if (authors.length === 0) return null;
  return (
    <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-stone-500">
        Similar Authors
      </h3>
      <div className="grid gap-3 sm:grid-cols-3">
        {authors.map((a) => (
          <Link
            key={a.name}
            href={`/authors/${encodeURIComponent(a.name)}`}
            className="flex flex-col rounded-2xl border border-stone-100 bg-stone-50 px-4 py-3 transition hover:border-stone-300 hover:bg-white active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
          >
            <span className="text-sm font-medium text-stone-800 leading-snug line-clamp-2">
              {a.name}
            </span>
            <span className="mt-1 text-xs text-stone-400">
              {a.quoteCount} quote{a.quoteCount !== 1 ? "s" : ""}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-4 h-4 w-24 animate-pulse rounded-full bg-stone-100" />
      <div className="space-y-3">
        {[80, 60, 45].map((w) => (
          <div key={w} className="space-y-1.5">
            <div className="flex justify-between">
              <div className="h-3 w-20 animate-pulse rounded-full bg-stone-100" />
              <div className="h-3 w-8 animate-pulse rounded-full bg-stone-100" />
            </div>
            <div className="h-2 w-full animate-pulse rounded-full bg-stone-100" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AuthorPage() {
  const params = useParams<{ name: string }>();
  const authorName = decodeURIComponent(params.name ?? "");

  const queryParams = {
    author: authorName,
    status: "approved" as const,
    pageSize: 100,
  };

  const { data, isLoading, isError } = useGetQuotes(queryParams, {
    query: { queryKey: getGetQuotesQueryKey(queryParams) },
  });

  const profileQuery = useGetAuthorProfile(authorName, {
    query: { queryKey: getGetAuthorProfileQueryKey(authorName) },
  });

  // Sort chronologically by year (nulls last), then alphabetically by text for stability
  const quotes = [...(data?.data ?? [])].sort((a, b) => {
    if (a.year == null && b.year == null) return 0;
    if (a.year == null) return 1;
    if (b.year == null) return -1;
    return a.year - b.year;
  });

  const quoteCount = !isLoading ? quotes.length : null;
  usePageMeta({
    title:
      quoteCount !== null
        ? `${authorName} · ${quoteCount} quote${quoteCount !== 1 ? "s" : ""} — QuotedsArrow`
        : `${authorName} quotes — QuotedsArrow`,
    description:
      quoteCount !== null
        ? `Explore ${quoteCount} quote${quoteCount !== 1 ? "s" : ""} from ${authorName} on QuotedsArrow.`
        : `Explore quotes from ${authorName} on QuotedsArrow.`,
  });

  const themeClusters = profileQuery.data?.themeClusters ?? [];
  const similarAuthors = profileQuery.data?.similarAuthors ?? [];
  const profileLoading = profileQuery.isLoading;
  const profilePending = !profileLoading && (profileQuery.data?.isPending ?? false);
  const profileInsufficient =
    !profileLoading &&
    !profilePending &&
    profileQuery.data !== undefined &&
    themeClusters.length === 0;

  return (
    <section data-testid="page-author">
      <Link
        href="/library"
        className="mb-4 inline-flex items-center gap-1.5 rounded-xl px-2 py-1 text-sm font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-800 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
      >
        ← Library
      </Link>
      <div className="mb-6 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="mb-1 text-sm font-medium text-stone-500">Author</div>
        <h2 className="text-2xl font-semibold tracking-tight text-stone-900">{authorName}</h2>
        {!isLoading && (
          <p className="mt-1 text-sm text-stone-500">
            {quotes.length} quote{quotes.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      {profileLoading ? (
        <ProfileSkeleton />
      ) : profilePending ? (
        <ProfilePendingCard />
      ) : profileInsufficient ? (
        <ProfileInsufficientDataCard />
      ) : (
        <>
          <ThemeProfileSection clusters={themeClusters} />
          <SimilarAuthorsSection authors={similarAuthors} />
        </>
      )}

      {isLoading ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="animate-pulse text-sm text-stone-400">Loading quotes…</p>
        </div>
      ) : isError ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">Could not load quotes for this author.</p>
        </div>
      ) : quotes.length === 0 ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">No approved quotes found for "{authorName}".</p>
          <Link href="/" className="mt-4 inline-block rounded-xl px-2 py-1 text-sm font-medium text-stone-900 transition hover:bg-stone-100 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1">
            ← Back to home
          </Link>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {quotes.map((q) => (
            <QuoteCard key={q.id} quote={toCardQuote(q)} />
          ))}
        </div>
      )}
    </section>
  );
}
